/**
 * canvasReplace — le contrat du mode « partout » de `replace_text`
 * (`everywhere:true`) : ses bornes, la garde qui le reconnaît, et ce que son
 * reçu rend.
 *
 * WHY un module à part d'`actionCatalog.ts` : le catalogue l'importe pour son
 * schéma d'entrée et son reçu, et le navigateur comme le backend en lisent les
 * bornes et la garde. Ce module n'importe donc RIEN du catalogue — la garde
 * prend un type structurel — pour ne pas fermer un cycle d'imports.
 */

import { z } from 'zod';

/** Nombre maximal de couples ancien / nouveau par appel. Valeur de jugement : celle d'une liste de textes de `format_text` (`CANVAS_FORMAT_TEXTS_MAX`). */
export const CANVAS_REPLACE_EVERYWHERE_PAIRS_MAX = 50;

/**
 * Nombre maximal d'occurrences écrites par appel : la somme des `expectedCount`.
 *
 * Un appel écrit par UN plan atomique, une étape par occurrence. La borne est
 * celle du chemin de production : `apply_edit_plan` plafonne ses plans à 50
 * étapes, et aucun plan de `text.rewrite` plus long n'a été mesuré. Le moteur
 * en admettrait 200 (`MAX_PLAN_STEPS`, typages publics de SuperDoc 2.15.1,
 * `document-api/src/types/mutation-plan.types.d.ts`, lu le 2026-10-08) : le
 * relevé R7 de `tests/ws-mocked/mesure-replace-text-partout.spec.ts` décidera
 * de relever cette borne, jamais au-delà de ce plafond.
 */
export const CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX = 50;

/** Nombre maximal d'emplacements rendus par couple dans un reçu de refus : assez pour que l'agent tranche. Valeur de jugement. */
export const CANVAS_REPLACE_LOCATIONS_MAX = 10;

/** Longueur maximale d'un texte cité dans le reçu (`find`, `replace`, extrait d'un emplacement). Valeur de jugement : assez pour le reconnaître, jamais un paragraphe. */
export const CANVAS_REPLACE_EXCERPT_MAX = 80;

/**
 * Pourquoi une occurrence trouvée ne peut pas être écrite par ce mode :
 *
 * - `no_node_id` — le fichier ne donne pas d'identifiant à son paragraphe ;
 * - `duplicate_node_id` — un autre paragraphe porte le même identifiant ;
 * - `table_cell` — son paragraphe est dans une cellule de tableau ;
 * - `not_unique_in_block` — son texte n'est pas unique dans son paragraphe.
 */
export const CanvasReplaceOutOfReachReasonSchema = z.enum(['no_node_id', 'duplicate_node_id', 'table_cell', 'not_unique_in_block']);
export type CanvasReplaceOutOfReachReason = z.infer<typeof CanvasReplaceOutOfReachReasonSchema>;

/** Les espaces que la typographie française pose à la place d'une espace simple : insécable, de chiffre, fine, fine insécable. */
const ESPACES_TYPOGRAPHIQUES = '\u00a0\u2007\u2009\u202f';
/** Les apostrophes courbes et la lettre apostrophe, que le modèle recopie en apostrophe droite. */
const APOSTROPHES_TYPOGRAPHIQUES = '\u2018\u2019\u02bc';

/** Vrai pour une espace simple ou typographique. */
export const estUneEspaceDeTexte = (caractere: string): boolean => caractere === ' ' || ESPACES_TYPOGRAPHIQUES.includes(caractere);
/** Vrai pour une apostrophe droite ou typographique. */
export const estUneApostropheDeTexte = (caractere: string): boolean => caractere === "'" || APOSTROPHES_TYPOGRAPHIQUES.includes(caractere);

/**
 * Ramène les espaces typographiques à l'espace simple et les apostrophes
 * courbes à l'apostrophe droite, UN caractère pour un : le texte plié a la
 * longueur du texte d'origine, une position trouvée sur l'un vaut sur l'autre.
 *
 * WHY ici : le mode partout de `replace_text` cherche à la typographie près —
 * le modèle recopie « 1 500 € » en espaces simples quand l'acte porte des
 * insécables. Le contrat (deux `find` égaux une fois pliés) et le navigateur
 * (la recherche) doivent plier de la même façon. `normalizeSearchText`
 * (`@hector/agent-text-normalizer`) ne conserve pas les positions.
 */
export function plierLaTypographie(texte: string): string {
  let plie = '';
  for (const caractere of texte) plie += caractere !== ' ' && estUneEspaceDeTexte(caractere) ? ' ' : caractere !== "'" && estUneApostropheDeTexte(caractere) ? "'" : caractere;
  return plie;
}

const TexteCiteSchema = z.string().max(CANVAS_REPLACE_EXCERPT_MAX);

/**
 * Ce qu'un `replace_text` en mode partout rend de plus que le reçu commun.
 *
 * Sur un reçu `ok`, `pairs` est vide et `occurrencesWritten` vaut la somme des
 * `expectedCount`. Sur un refus, `occurrencesWritten` vaut 0 et `pairs` ne
 * porte QUE les couples à trancher : ceux dont le compte diffère, ou dont une
 * occurrence est hors de portée.
 */
export const CanvasReplaceEverywhereReceiptSchema = z
  .object({
    /** Où les textes ont été cherchés : le corps et ses tableaux. En-têtes, pieds de page et notes de bas de page ne le sont pas. */
    scope: z.literal('body_and_tables'),
    pairsGiven: z.number().int().min(1).max(CANVAS_REPLACE_EVERYWHERE_PAIRS_MAX),
    occurrencesWritten: z.number().int().nonnegative().max(CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX),
    pairs: z
      .array(
        z
          .object({
            find: TexteCiteSchema,
            replace: TexteCiteSchema,
            expectedCount: z.number().int().min(1).max(CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX),
            /** Occurrences en mot entier trouvées dans la portée, celles hors de portée comprises. */
            found: z.number().int().nonnegative(),
            /** Fois où le texte n'apparaît que dans un mot ou un nombre plus long : pas des occurrences. */
            insideLongerWord: z.number().int().positive().optional(),
            /** Parmi `found`, les occurrences que ce mode ne peut pas écrire ; chaque emplacement dit pourquoi. */
            outOfReach: z.number().int().positive().optional(),
            /** Fois où le texte est couvert par le `find` plus long d'un autre couple de l'appel : pas comptées dans `found`. */
            coveredByLongerPair: z.number().int().positive().optional(),
            locations: z
              .array(
                z
                  .object({
                    /** L'identifiant du bloc qui porte l'occurrence ; `null` quand le fichier ne lui en donne pas. */
                    nodeId: z.string().min(1).nullable(),
                    excerpt: TexteCiteSchema,
                    inTable: z.literal(true).optional(),
                    outOfReach: CanvasReplaceOutOfReachReasonSchema.optional(),
                  })
                  .strict(),
              )
              .max(CANVAS_REPLACE_LOCATIONS_MAX),
            /** Emplacements trouvés au-delà des `locations` rendus. */
            locationsNotListed: z.number().int().positive().optional(),
          })
          .strict(),
      )
      .max(CANVAS_REPLACE_EVERYWHERE_PAIRS_MAX),
  })
  .strict();
export type CanvasReplaceEverywhereReceipt = z.infer<typeof CanvasReplaceEverywhereReceiptSchema>;

/**
 * Vrai pour un `replace_text` en mode partout : seul `everywhere:true` l'ouvre.
 *
 * Le type est structurel pour que ce module n'importe pas le catalogue ; le
 * prédicat de type laisse l'appelant garder la branche `replace_text` de SON
 * union d'actions.
 */
export function estUnRemplacementPartout<Action extends { action: string; everywhere?: boolean }>(
  action: Action,
): action is Action & { action: 'replace_text'; everywhere: true } {
  return action.action === 'replace_text' && action.everywhere === true;
}
